<template>
  <div>
    <p class="cm-mg-p-wzd">1. {{ $t('common_words.general_information') }}</p>
    <hr />
    <div class="row">
      <div class="col-12 col-md-6">
        <div class="form-group has-label">
          <label>{{ $t('common_words.company') }}: *</label>
          <el-select
            v-model="model.business_id"
            clearable
            filterable
            class="select-primary custom-width-select required-business shadow-sm"
            size="large"
            :placeholder="`${$t('common_words.select')}...`"
            @change="onBusinessChange"
          >
            <el-option
              class="select-primary"
              v-for="business in businesses"
              :key="business.id"
              :value="business.id"
              :label="business.name"
            />
          </el-select>
        </div>
      </div>
      <div class="col-12 col-md-6">
        <div class="form-group has-label">
          <label>{{ $t('common_words.farm') }}: *</label>
          <el-select
            v-model="model.farm_id"
            clearable
            filterable
            class="select-primary custom-width-select required-farm shadow-sm"
            size="large"
            :placeholder="`${$t('common_words.select')}...`"
            @change="onFarmChange"
          >
            <el-option
              class="select-primary"
              v-for="farm in filteredFarms"
              :key="farm.id"
              :value="farm.id"
              :label="farm.name"
            />
          </el-select>
        </div>
      </div>
    </div>
    <p class="cm-mg-p-wzd text-center text-muted">
      {{ $t('devices.blood_pressure_monitors_creation') }}
    </p>
    <hr />

    <div
      v-for="(_, index) in model.names"
      :key="`blood-pressure-monitor-${index}`"
      class="row align-items-start border rounded-sm shadow-sm py-3 px-2 mb-3 monitor-row"
    >
      <div class="col-12 col-md-4">
        <div class="form-group has-label">
          <label>{{ $t('common_words.valve') }}: *</label>
          <el-select
            v-model="model.valve_ids[index]"
            clearable
            filterable
            class="select-primary custom-width-select shadow-sm"
            size="large"
            :disabled="!model.farm_id"
            :placeholder="`${$t('common_words.select')}...`"
            @change="updateSuggestedName(index)"
          >
            <el-option
              class="select-primary"
              v-for="valve in valves"
              :key="valve.id"
              :value="valve.id"
              :label="valve.name"
            />
          </el-select>
          <span
            v-if="rowErrors[index] && !model.valve_ids[index]"
            class="text-danger small"
          >
            {{ $t('common_words.required_field') }}
          </span>
        </div>
      </div>
      <div class="col-12 col-md-3">
        <fg-input
          v-model.number="model.depths[index]"
          type="number"
          min="0"
          step="0.01"
          :label="`${$t('common_words.depth')}: *`"
          :name="`common_words.depth_${index}`"
          :error="getFieldErrorByIndex('common_words.depth', true, index)"
          v-validate="modelValidations.depths"
          @input="updateSuggestedName(index)"
        />
      </div>
      <div class="col-10 col-md-4">
        <fg-input
          v-model.trim="model.names[index]"
          type="text"
          :label="`${$t('common_words.name')}: *`"
          :name="`common_words.name_${index}`"
          :error="getFieldErrorByIndex('common_words.name', true, index)"
          v-validate="modelValidations.names"
        />
      </div>
      <div class="col-2 col-md-1 pt-4 text-center">
        <a
          v-tooltip.top-center="$t('common_words.add')"
          class="btn-success btn-simple btn-link custom-margin-actions"
          @click="addMonitor(index)"
        >
          <em class="fa fa-plus custom-size-fa-actions"></em>
        </a>
        <a
          v-if="model.names.length > 1"
          v-tooltip.top-center="$t('common_words.erase')"
          class="btn-danger btn-simple btn-link custom-margin-actions"
          @click="removeMonitor(index)"
        >
          <em class="fa fa-times custom-size-fa-actions"></em>
        </a>
      </div>
    </div>
  </div>
</template>

<script>
import { Axios } from 'src/class/Axios.js';
import { isset } from 'src/util/Util';
import MixinAlert from 'src/mixins/mixin-alert';
import MixinForm from 'src/mixins/forms/mixin-form';
import MixinLoading from 'src/mixins/mixin-loading';
import MixinPermissions from 'src/util/mixinPermissions';

export default {
  mixins: [MixinAlert, MixinForm, MixinLoading, MixinPermissions],
  data() {
    return {
      businesses: [],
      farms: [],
      valves: [],
      rowErrors: [],
      suggestedNames: [null],
      model: {
        business_id: null,
        farm_id: null,
        ids: [null],
        valve_ids: [null],
        depths: [null],
        names: [null],
      },
      modelValidations: {
        depths: {
          required: true,
          decimal: 2,
          min_value: 0,
        },
        names: {
          required: true,
        },
      },
    };
  },
  computed: {
    filteredFarms() {
      if (!isset(this.model.business_id)) return this.farms;
      return this.farms.filter((farm) => {
        if (farm.business_id == this.model.business_id) return true;
        const relations = farm.businesses || farm.business || [];
        const businesses = Array.isArray(relations) ? relations : [relations];
        if (
          !isset(farm.business_id) &&
          businesses.filter(Boolean).length === 0
        ) {
          return true;
        }
        return businesses.some(
          (business) => business && business.id == this.model.business_id
        );
      });
    },
  },
  methods: {
    onBusinessChange() {
      this.model.farm_id = null;
      this.valves = [];
      this.model.valve_ids = this.model.valve_ids.map(() => null);
      this.refreshSuggestedNames();
    },
    onFarmChange(farmId) {
      this.valves = [];
      this.model.valve_ids = this.model.valve_ids.map(() => null);
      this.refreshSuggestedNames();
      if (isset(farmId)) this.loadValves(farmId);
    },
    loadValves(farmId) {
      return Axios.getMasters(`irrigation/valves?farm_id=${farmId}`)
        .then((response) => {
          this.valves = response.data.data;
        })
        .catch((error) => this.showMessageErrors(error));
    },
    addMonitor(index) {
      ['ids', 'valve_ids', 'depths', 'names'].forEach((field) =>
        this.model[field].splice(index + 1, 0, null)
      );
      this.suggestedNames.splice(index + 1, 0, null);
      this.rowErrors.splice(index + 1, 0, false);
    },
    removeMonitor(index) {
      ['ids', 'valve_ids', 'depths', 'names'].forEach((field) =>
        this.model[field].splice(index, 1)
      );
      this.suggestedNames.splice(index, 1);
      this.rowErrors.splice(index, 1);
    },
    getSuggestedName(index) {
      const valve = this.valves.find(
        (item) => item.id == this.model.valve_ids[index]
      );
      const depth = this.model.depths[index];
      if (!valve || depth === null || depth === '' || depth === undefined) {
        return null;
      }
      return `T${depth}-${valve.name}`;
    },
    updateSuggestedName(index) {
      const suggestion = this.getSuggestedName(index);
      const currentName = this.model.names[index];
      if (!currentName || currentName === this.suggestedNames[index]) {
        this.$set(this.model.names, index, suggestion);
      }
      this.$set(this.suggestedNames, index, suggestion);
    },
    refreshSuggestedNames() {
      this.model.names.forEach((_, index) => this.updateSuggestedName(index));
    },
    validate() {
      return this.$validator.validateAll().then((valid) => {
        this.rowErrors = this.model.valve_ids.map((valveId) => !isset(valveId));
        if (!valid || this.rowErrors.some(Boolean)) return false;
        if (!isset(this.model.business_id)) {
          return this.validationInputsRequired('business');
        }
        if (!isset(this.model.farm_id)) {
          return this.validationInputsRequired('farm');
        }

        const business = this.businesses.find(
          (item) => item.id == this.model.business_id
        );
        const farm = this.farms.find((item) => item.id == this.model.farm_id);
        const valveNames = this.model.valve_ids.map((valveId) => {
          const valve = this.valves.find((item) => item.id == valveId);
          return valve ? valve.name : null;
        });

        this.$emit('on-validated', {
          ...this.model,
          business_name: business ? business.name : null,
          farm_name: farm ? farm.name : null,
          valve_names: valveNames,
        });
        return true;
      });
    },
    setEditForm(editForm) {
      const business = editForm.business || (editForm.businesses || [])[0];
      const monitors = editForm.blood_pressure_monitors || editForm.monitors;
      const rows =
        Array.isArray(monitors) && monitors.length ? monitors : [editForm];

      this.model = {
        business_id: editForm.business_id || (business && business.id) || null,
        farm_id:
          editForm.farm_id || (editForm.farm && editForm.farm.id) || null,
        ids: Array.isArray(editForm.ids)
          ? editForm.ids
          : rows.map((row) => row.id || null),
        valve_ids: Array.isArray(editForm.valve_ids)
          ? editForm.valve_ids
          : rows.map(
              (row) => row.valve_id || (row.valve && row.valve.id) || null
            ),
        depths: Array.isArray(editForm.depths)
          ? editForm.depths
          : rows.map((row) => row.depth),
        names: Array.isArray(editForm.names)
          ? editForm.names
          : rows.map((row) => row.name),
      };
      this.suggestedNames = this.model.names.map(() => null);
      this.rowErrors = this.model.names.map(() => false);
      if (isset(this.model.farm_id)) this.loadValves(this.model.farm_id);
    },
  },
  mounted() {
    Promise.all([
      Axios.getMasters('businesses/only'),
      Axios.getMasters('farms/farms/only'),
    ])
      .then(([businesses, farms]) => {
        this.businesses = businesses.data.data;
        this.farms = farms.data.data;
      })
      .catch((error) => this.showMessageErrors(error));

    if (this.isUpdate() && sessionStorage.getItem('editForm')) {
      this.setEditForm(JSON.parse(sessionStorage.getItem('editForm')));
    }
  },
};
</script>

<style scoped>
.monitor-row {
  background-color: #fcfffb !important;
}
</style>
